import { MaintenancePointApi } from 'api/MaintenancePointApi'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { ReactComponent as DebrisDumpSvg } from 'images/icons/debris_dump.svg'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { ReactComponent as WasteWaterDrainSvg } from 'images/icons/waste_water_drain.svg'
import AddMaintenancePointModal from 'pages/Assets/maintenance/AddMaintenancePointModal/AddMaintenancePointModal'
import tableStyle from 'pages/Assets/maintenance/AssetsMaintenanceTable.module.css'
import { maintenancePointAvailableActions } from 'pages/Assets/maintenance/maintenancePointUtils'
import { FunctionComponent, SVGProps, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import getTestId from 'utils/getTestId'
import { IMaintenancePoint } from 'utils/interfaces'
import { ACTION_TYPE_LABELS, ActionType } from 'utils/servicePointsCatalog'

const ACTION_INFO_ICONS: Partial<Record<ActionType, FunctionComponent<SVGProps<SVGSVGElement>>>> = {
  [ActionType.WASTE_WATER_DRAIN]: WasteWaterDrainSvg,
  [ActionType.DEBRIS_DUMP]: DebrisDumpSvg,
}

const MaintenancePointItem = ({
  point,
  points,
  facilityId,
  highlighted,
  onPointUpdated,
  hideDelete,
}: {
  point: IMaintenancePoint
  points: IMaintenancePoint[]
  facilityId: number
  highlighted: boolean
  onPointUpdated: () => void
  hideDelete?: boolean
}) => {
  const { t } = useTranslation()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editing, setEditing] = useState(false)

  const availableActions = useMemo(() => maintenancePointAvailableActions(point.type), [point.type])
  const deleteBlockedReason = point.cannot_delete_reason || undefined

  const deletePoint = async () => {
    try {
      await MaintenancePointApi.deleteMaintenancePoint(point.id)
      setDeleteDialogOpen(false)
      onPointUpdated()
    } catch (error) {
      console.warn('Error deleting maintenance point', error)
    }
  }

  return (
    <>
      <div className={clsx(tableStyle.tableRow, { [tableStyle.highlighted]: highlighted })} key={point.id}>
        <span />
        <span className={tableStyle.tableCol}>{point.name}</span>
        <span className={clsx(tableStyle.tableCol, tableStyle.actionsCol)}>
          {availableActions.length ? (
            <span className={tableStyle.actionIcons} aria-label="Available actions">
              {availableActions.map(action => {
                const ActionIcon = ACTION_INFO_ICONS[action]
                const label = ACTION_TYPE_LABELS[action] ?? ActionType[action] ?? String(action)
                if (!ActionIcon) {
                  return (
                    <span key={action} className={tableStyle.actionIconFallback} title={label}>
                      {label}
                    </span>
                  )
                }
                return (
                  <span key={action} className={tableStyle.actionIconWrap} title={label}>
                    <ActionIcon className={tableStyle.actionIcon} aria-hidden />
                    <span className={tableStyle.srOnly}>{label}</span>
                  </span>
                )
              })}
            </span>
          ) : (
            '—'
          )}
        </span>
        <div className={tableStyle.colCenter}>
          <ActionsMenu>
            <ActionsMenuItem onClick={() => setEditing(true)} data-test-id={getTestId('edit-maintenance-point')}>
              <span className={tableStyle.menuItemIcon} />
              <span className={tableStyle.menuItemText}>{t('common.edit')}</span>
            </ActionsMenuItem>
            {!hideDelete && (
              <ActionsMenuItem
                onClick={() => setDeleteDialogOpen(true)}
                data-test-id={getTestId('remove-maintenance-point')}
                disabled={Boolean(deleteBlockedReason)}
                title={deleteBlockedReason}
              >
                <RemoveSvg className={tableStyle.menuItemIcon} />
                <span className={tableStyle.menuItemText}>{t('common.delete')}</span>
              </ActionsMenuItem>
            )}
          </ActionsMenu>
        </div>
      </div>
      <ConfirmDialog
        title="Delete maintenance point?"
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirmClick={deletePoint}
        confirmText={t('common.delete')}
        danger={true}
      />
      <AddMaintenancePointModal
        isOpen={editing}
        facilityId={facilityId}
        points={points}
        pointExisting={point}
        onClose={() => setEditing(false)}
        onSave={() => {
          setEditing(false)
          onPointUpdated()
        }}
      />
    </>
  )
}

export default MaintenancePointItem
